import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import {
  Search,
  X,
  Building2,
  Phone,
  Settings,
  Tag,
  History,
  Info,
  ArrowRight,
  Edit,
  FileText,
  UserPlus,
  AlertTriangle,
  Activity,
  User,
  ArrowDownToLine,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  primaryContactWarning,
  STRATEGY_EMAIL_NO_PRIMARY_CONTACT_WARNING,
  strategyHasEmailAction,
} from '@/lib/primary-contact-warnings';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { formatCurrency, formatDateTime, cn } from '@/lib/utils';
import { findTemplateByStoredId, logicalTemplateId } from '@/lib/template-identity';
import { type RiskLevelCode, getRiskBadgeClassName, getRiskLabel } from '@/lib/risk-level';
import { AllContactsTab } from '@/components/customers/all-contacts-tab';
import { AddressesTab } from '@/components/customers/addresses-tab';
import { CommunicationHistoryTab } from '@/components/customers/communication-history-tab';
import { CustomerMetricCards } from '@/components/customers/customer-metric-cards';
import { CollectionsTab } from '@/components/customers/collections-tab';
import { PromisesTab } from '@/components/customers/promises-tab';
import { DisputesTab } from '@/components/customers/disputes-tab';
import { AgeingBarChart } from '@/components/customers/ageing-bar-chart';
import { NotesTab } from '@/components/customers/notes-tab';
import { DocumentsTab } from '@/components/customers/documents-tab';
import { CurrencySelect, type CurrencyMode } from '@/components/shared/currency-select';
import { RequiresAttentionPanel } from '@/components/customer-detail/requires-attention-panel';
import { RecentActivityPanel } from '@/components/customer-detail/recent-activity-panel';
import { UserAvatar } from '@/components/user-avatar';
import { mockUsers } from '@/lib/mock-users';
import { OnStopNoteDialog } from '@/components/customers/on-stop-note-dialog';
import { useGeneralSettings } from '@/hooks/useGeneralSettings';
import { useAddCustomerNote } from '@/hooks/useCustomerNotes';
import { useCustomerDetails, useCustomerPayments } from '@/hooks/useCustomerDetailData';
import { useCustomerAgeing } from '@/hooks/useCustomerAgeing';
import { useAgeingBands } from '@/hooks/useAgeingBands';
import { useTransactionDetail } from '@/hooks/useTransactionsData';
import {
  useCustomerTransactionsGraphQL,
  useCustomerCompletedTransactionsGraphQL,
  useCustomerMetricTotals,
} from '@/hooks/useTransactionsGraphQL';
import { useStrategiesList } from '@/hooks/useStrategiesData';
import { useTemplatesList } from '@/hooks/useTemplatesData';
import { applyCollectionSettingsToMockCustomer } from '@/services/demo/customer-collection-settings-demo.service';
import { useRiskLevels } from '@/hooks/useRiskLevelsData';
import { useCompanyUsersList } from '@/hooks/useUsersData';
import type { CustomerDetailUI } from '@/types/ui/customer-detail';
import type { Transaction, ColumnVisibility } from '@/types/transaction';
import { useApi } from '@/hooks/useApi';
import { useMode } from '@/providers/ModeProvider';
import { useCompany } from '@/contexts/CompanyContext';
import { useQueryClient } from '@tanstack/react-query';
import { TransactionTable } from '@/components/transactions/transaction-table';
import { TransactionDetailSheet } from '@/components/transactions/transaction-detail-sheet';
import { ContextualCreateTaskDrawer } from '@/components/tasks/contextual-create-task-drawer';
import { SelectionToolbar } from '@/components/transactions/selection-toolbar';
import { SendRemindersDialog } from '@/components/transactions/send-reminders-dialog';
import { ColumnConfigDialog, type ColumnDefinition } from '@/components/shared/column-config-dialog';
import { useScreenColumnPreferences } from '@/hooks/useScreenColumnPreferences';
import { UniqueTableNamesForColumnConfiguration } from '@/config/unique-table-names-for-column-configuration';
import { TransactionSendCopyInvoiceModal } from '@/components/transactions/transaction-send-copy-invoice-modal';
import {
  CreateDisputeModal,
  AddNoteModal,
  type DisputeFormData,
  type NoteFormData,
} from '@/components/transactions/transaction-action-modals';
import { CreateCaseDrawer } from '@/components/collections/create-case-drawer';
import { CreatePromiseDrawer, type PromiseFormData } from '@/components/collections/create-promise-drawer';
import { calculateTransactionTotals } from '@/lib/export-utils';
import { invalidateCustomerListQueries } from '@/lib/query/customer-list-queries';
import { useExportAll } from '@/hooks/useExportAll';
import { ExportProvider } from '@/providers/ExportProvider';
import type { ColumnInput } from '@/types/export';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AllocationHistoryModal } from '@/components/transactions/allocation-history-modal';
import { useCustomerAuditLogs } from '@/hooks/useCustomerAuditLogs';
import type { CustomerAuditLog } from '@/types/collections';

import { ScopeLabel } from '@/components/customers/scope-label';
import { HelpSupportButton } from '@/components/help-support';

// Customer type alias available for future use
void (null as unknown as CustomerDetailUI);

// Types
interface Payment {
  id: string;
  date: string;
  amount: number;
  method: string;
  reference: string;
  allocations: {
    invoiceId: string;
    invoiceDate: string;
    amount: number;
  }[];
}

// Mock Payments (kept for potential future use)
const mockPayments: Record<string, Payment[]> = {
  '1': [
    {
      id: 'PAY-001',
      date: '2023-11-15',
      amount: 50000,
      method: 'Wire Transfer',
      reference: 'WT-2023-001',
      allocations: [
        { invoiceId: 'INV-001', invoiceDate: '2023-10-01', amount: 30000 },
        { invoiceId: 'INV-002', invoiceDate: '2023-10-15', amount: 20000 },
      ],
    },
    {
      id: 'PAY-002',
      date: '2023-10-20',
      amount: 35000,
      method: 'ACH',
      reference: 'ACH-2023-002',
      allocations: [{ invoiceId: 'INV-003', invoiceDate: '2023-09-01', amount: 35000 }],
    },
  ],
};

// Mark mock data as intentionally kept for future use
void mockPayments;

const DEFAULT_CUSTOMER_DETAIL_TX_COLUMNS: ColumnDefinition[] = [
  { id: 'referenceNumber', label: 'Reference Number', visible: true, order: 0 },
  { id: 'transactionDate', label: 'Transaction Date', visible: true, order: 1 },
  { id: 'dueDate', label: 'Due Date', visible: true, order: 2 },
  { id: 'anticipatedDate', label: 'Anticipated Date', visible: true, order: 3 },
  { id: 'customerName', label: 'Customer Name', visible: false, order: 4 },
  { id: 'transactionType', label: 'Type', visible: true, order: 5 },
  { id: 'currency', label: 'Currency', visible: true, order: 6 },
  { id: 'amount', label: 'Gross', visible: true, order: 7 },
  { id: 'outstandingAmount', label: 'Outstanding', visible: true, order: 8 },
  { id: 'status', label: 'Status', visible: true, order: 9 },
  { id: 'description', label: 'Description', visible: false, order: 10 },
  { id: 'createdBy', label: 'Created By', visible: false, order: 11 },
  { id: 'modifiedBy', label: 'Modified By', visible: false, order: 12 },
  { id: 'accountCode', label: 'Account', visible: false, order: 13 },
  { id: 'netAmount', label: 'Net', visible: false, order: 14 },
  { id: 'vatAmount', label: 'Tax', visible: false, order: 15 },
  { id: 'flags', label: 'Flags', visible: true, order: 16 },
  { id: 'onDispute', label: 'External Dispute', visible: true, order: 17 },
  { id: 'subLedger', label: 'Sub Ledger', visible: false, order: 18 },
  { id: 'orderNumber', label: 'Order Number', visible: false, order: 19 },
  { id: 'actions', label: 'Actions', visible: false, order: 20 },
];

const getCustomerAuditLogIcon = (action: CustomerAuditLog['action']) => {
  switch (action) {
    case 'on_stop_change':
      return AlertTriangle;
    case 'collection_strategy_change':
      return Settings;
    case 'credit_controller_change':
      return UserPlus;
    case 'account_manager_change':
      return UserPlus;
    case 'risk_level_change':
      return ArrowRight;
    case 'note_added':
    case 'note_updated':
    case 'note_deleted':
      return FileText;
    case 'field_update':
      return Edit;
    default:
      return Activity;
  }
};

const getRiskValueBadgeClass = (value: string | number | null | undefined): string | null => {
  if (!value) return null;
  const normalized = String(value).toLowerCase().trim();
  const knownLevels = ['critical', 'high', 'medium', 'low'];
  const match = knownLevels.find((level) => normalized.includes(level));
  if (!match) return null;
  return `${getRiskBadgeClassName(match as RiskLevelCode)} border-current text-xs`;
};

const getCustomerAuditLogColor = (action: CustomerAuditLog['action']) => {
  switch (action) {
    case 'on_stop_change':
      return 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400';
    case 'collection_strategy_change':
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400';
    case 'credit_controller_change':
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400';
    case 'account_manager_change':
      return 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400';
    case 'risk_level_change':
      return 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400';
    case 'note_added':
    case 'note_updated':
    case 'note_deleted':
      return 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400';
    case 'field_update':
      return 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400';
    default:
      return 'bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400';
  }
};

const TRANSACTIONS_TAB = 'transactions';

/** Maps the legacy `invoices` tab value to its current name so existing links keep working. */
const normalizeTabParam = (tab: string | null): string => {
  if (!tab) return 'overview';
  return tab === 'invoices' ? TRANSACTIONS_TAB : tab;
};

export function CustomerDetailPage() {
  return (
    <ExportProvider>
      <CustomerDetailPageInner />
    </ExportProvider>
  );
}

function CustomerDetailPageInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { customerId } = useParams<{ customerId: string }>();
  const { apiPut } = useApi();
  const { mode, isDemo } = useMode();
  const { selectedCompany } = useCompany();
  const queryClient = useQueryClient();

  const { data: strategies = [] } = useStrategiesList();
  const { data: templates = [] } = useTemplatesList();
  const { data: users = [] } = useCompanyUsersList();
  const { data: riskLevels = [] } = useRiskLevels();
  const { data: customerAuditLogs = [] } = useCustomerAuditLogs(customerId);

  // Fetch customer data using mode-aware hooks
  const { data: customer, isLoading, error: customerError } = useCustomerDetails(customerId);
  const { data: payments = [] } = useCustomerPayments(customerId);

  const [editedCollectionStrategyId, setEditedCollectionStrategyId] = useState('');
  const [editedStatementTemplateId, setEditedStatementTemplateId] = useState('none');
  const [editedRiskLevelId, setEditedRiskLevelId] = useState('');
  const [editedCreditControllerId, setEditedCreditControllerId] = useState('');
  const [editedAccountManagerId, setEditedAccountManagerId] = useState('');
  const [onStopValue, setOnStopValue] = useState<boolean>(false);
  const [onStopNoteDialogOpen, setOnStopNoteDialogOpen] = useState(false);
  const [pendingOnStopValue, setPendingOnStopValue] = useState<boolean | null>(null);
  const [strategyEmailWarningOpen, setStrategyEmailWarningOpen] = useState(false);

  const [activeTab, setActiveTab] = useState(() => normalizeTabParam(searchParams.get('tab')));
  const [activeTransactionTab, setActiveTransactionTab] = useState('outstanding');
  const [isConsolidatedView, setIsConsolidatedView] = useState(false);
  const isMultiCompanyGroup = selectedCompany?.isMultiCompanyGroup ?? false;

  const [detailCurrencyMode, setDetailCurrencyMode] = useState<CurrencyMode>('customer');

  /**
   * For "Any Currency" accounts this holds the currently selected currency
   * in the currency dropdown on the detail header.
   * Base currency means show all transactions; any other value filters to that currency.
   */
  const [selectedAnyCurrency, setSelectedAnyCurrency] = useState<string>(selectedCompany?.currency ?? 'GBP');

  const ageingCurrency = customer?.isAnyCurrency
    ? selectedAnyCurrency
    : detailCurrencyMode === 'customer'
      ? customer?.currency
      : (selectedCompany?.currency ?? customer?.currency);
  const { data: customerAgeing } = useCustomerAgeing(customerId, ageingCurrency, isConsolidatedView);
  const { data: ageingBands } = useAgeingBands();

  // Compute the effective currency for the Financial tab metric totals.
  // When a non-base currency is active, pass it so the hook fetches currency-specific totals.
  const financialCurrency = (() => {
    const base = selectedCompany?.currency ?? 'GBP';
    if (customer?.isAnyCurrency) {
      return selectedAnyCurrency !== base ? selectedAnyCurrency : undefined;
    }
    if (detailCurrencyMode === 'base') return undefined;
    return customer?.currency !== base ? customer?.currency : undefined;
  })();
  const { data: financialMetricTotals } = useCustomerMetricTotals(customerId, financialCurrency, true);

  // Reset currency toggle/any-currency selection/consolidated view whenever the customer changes
  useEffect(() => {
    setDetailCurrencyMode('customer');
    setSelectedAnyCurrency(selectedCompany?.currency ?? 'GBP');
    setIsConsolidatedView(false);
  }, [customerId]);

  // Search state (declared before hooks that reference them)
  const [outstandingSearchQuery, setOutstandingSearchQuery] = useState('');
  const [historySearchQuery, setHistorySearchQuery] = useState('');

  // Transaction table state for History tab
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(25);

  // Transaction table state for Outstanding tab
  const [outstandingCurrentPage, setOutstandingCurrentPage] = useState(1);
  const [outstandingPageSize, setOutstandingPageSize] = useState(25);
  const [outstandingSortBy, setOutstandingSortBy] = useState<string>('transactionDate');
  const [outstandingSortOrder, setOutstandingSortOrder] = useState<'asc' | 'desc'>('desc');
  const [historySortBy, setHistorySortBy] = useState<string>('transactionDate');
  const [historySortOrder, setHistorySortOrder] = useState<'asc' | 'desc'>('desc');

  // Fetch customer transactions with server-side pagination, search and sorting
  const {
    transactions: outstandingTransactions,
    pagination: outstandingPagination,
    summary: outstandingSummary,
    isLoading: transactionsLoading,
  } = useCustomerTransactionsGraphQL(customerId, {
    page: outstandingCurrentPage,
    pageSize: outstandingPageSize,
    search: outstandingSearchQuery || undefined,
    sortBy: outstandingSortBy,
    sortOrder: outstandingSortOrder,
    // For Any Currency accounts, filter transactions by the selected currency.
    // When base currency (GBP) is selected, no currency filter is applied (show all).
    currency:
      customer?.isAnyCurrency && selectedAnyCurrency !== (selectedCompany?.currency ?? 'GBP')
        ? selectedAnyCurrency
        : undefined,
    allCompanies: isConsolidatedView || undefined,
  });
  const {
    transactions: completedTransactions,
    pagination: historyPagination,
    summary: historySummary,
    isLoading: completedTransactionsLoading,
  } = useCustomerCompletedTransactionsGraphQL(customerId, {
    page: historyCurrentPage,
    pageSize: historyPageSize,
    search: historySearchQuery || undefined,
    sortBy: historySortBy,
    sortOrder: historySortOrder,
    // For Any Currency accounts, filter transactions by the selected currency.
    // When base currency (GBP) is selected, no currency filter is applied (show all).
    currency:
      customer?.isAnyCurrency && selectedAnyCurrency !== (selectedCompany?.currency ?? 'GBP')
        ? selectedAnyCurrency
        : undefined,
    allCompanies: isConsolidatedView || undefined,
  });

  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);

  // Fetch full transaction detail (with line items) when a transaction is selected.
  // The REST detail endpoint doesn't return display amounts, so preserve them from the
  // GraphQL-loaded selectedTransaction (which does have them).
  const { data: transactionDetail } = useTransactionDetail(selectedTransaction?.id ?? null);
  const detailTransaction = useMemo(() => {
    if (!transactionDetail) return selectedTransaction;
    return {
      ...transactionDetail,
      displayAmount: selectedTransaction?.displayAmount,
      displayNetAmount: selectedTransaction?.displayNetAmount,
      displayVatAmount: selectedTransaction?.displayVatAmount,
      displayOutstandingAmount: selectedTransaction?.displayOutstandingAmount,
    };
  }, [transactionDetail, selectedTransaction]);

  // Selection state for Outstanding tab
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [sendRemindersDialogOpen, setSendRemindersDialogOpen] = useState(false);
  const [sendCopyInvoiceDialogOpen, setSendCopyInvoiceDialogOpen] = useState(false);
  const [createCollectionCaseModalOpen, setCreateCollectionCaseModalOpen] = useState(false);
  const [createPromiseModalOpen, setCreatePromiseModalOpen] = useState(false);
  const [createDisputeModalOpen, setCreateDisputeModalOpen] = useState(false);
  const [addNoteModalOpen, setAddNoteModalOpen] = useState(false);
  const [allocationHistoryModalOpen, setAllocationHistoryModalOpen] = useState(false);
  const [createTaskDrawerOpen, setCreateTaskDrawerOpen] = useState(false);

  // Selection state for History tab
  const [historySelectedRows, setHistorySelectedRows] = useState<Set<number>>(new Set());
  const [historySendRemindersDialogOpen, setHistorySendRemindersDialogOpen] = useState(false);
  const [historySendCopyInvoiceDialogOpen, setHistorySendCopyInvoiceDialogOpen] = useState(false);
  const [historyCreateCollectionCaseModalOpen, setHistoryCreateCollectionCaseModalOpen] = useState(false);
  const [historyCreatePromiseModalOpen, setHistoryCreatePromiseModalOpen] = useState(false);
  const [historyCreateDisputeModalOpen, setHistoryCreateDisputeModalOpen] = useState(false);
  const [historyAddNoteModalOpen, setHistoryAddNoteModalOpen] = useState(false);
  const [historyAllocationHistoryModalOpen, setHistoryAllocationHistoryModalOpen] = useState(false);
  const [historyCreateTaskDrawerOpen, setHistoryCreateTaskDrawerOpen] = useState(false);

  // Payment expansion state
  const [expandedPayments, setExpandedPayments] = useState<string[]>([]);

  // Fetch general settings and add note mutation
  const { data: generalSettings } = useGeneralSettings();
  const addNoteMutation = useAddCustomerNote();

  // For demo purposes, use the first user as the current user
  const currentUser = mockUsers[0];

  const {
    columns: outstandingCols,
    savePreferences: saveOutstandingPrefs,
    resetToDefaults: resetOutstandingPrefs,
  } = useScreenColumnPreferences(
    UniqueTableNamesForColumnConfiguration.CustomerDetailOutstanding,
    DEFAULT_CUSTOMER_DETAIL_TX_COLUMNS,
  );
  const columnVisibility = useMemo(() => {
    const vis = {} as ColumnVisibility;
    outstandingCols.forEach((col) => {
      vis[col.id as keyof ColumnVisibility] = col.visible;
    });
    return vis;
  }, [outstandingCols]);

  const {
    columns: historyCols,
    savePreferences: saveHistoryPrefs,
    resetToDefaults: resetHistoryPrefs,
  } = useScreenColumnPreferences(
    UniqueTableNamesForColumnConfiguration.CustomerDetailHistory,
    DEFAULT_CUSTOMER_DETAIL_TX_COLUMNS,
  );
  const historyColumnVisibility = useMemo(() => {
    const vis = {} as ColumnVisibility;
    historyCols.forEach((col) => {
      vis[col.id as keyof ColumnVisibility] = col.visible;
    });
    return vis;
  }, [historyCols]);

  // Once customer data is loaded, ensure location.state carries the customer's
  // name and account number so the top-bar breadcrumb shows the right label.
  // This handles the case where the user navigated directly via URL (no state).
  useEffect(() => {
    if (!customer || isLoading) return;
    const state = location.state as { customer?: { name?: string; accountNumber?: string } } | null;
    if (state?.customer?.name === customer.name && state?.customer?.accountNumber === customer.accountNumber) return; // already correct, skip replace
    navigate(location.pathname + location.search, {
      replace: true,
      state: { customer: { name: customer.name, accountNumber: customer.accountNumber } },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id, isLoading]);

  // Map frontend column IDs to backend TransactionList property names for export
  const transactionExportColumnNameMap: Record<string, string> = useMemo(
    () => ({
      referenceNumber: 'ReferenceNumber',
      transactionDate: 'TransactionDate',
      dueDate: 'DueDate',
      anticipatedDate: 'AnticipatedDate',
      customerName: 'CustomerName',
      transactionType: 'TransactionTypeName',
      amount: 'DisplayAmount',
      outstandingAmount: 'DisplayOutstandingAmount',
      status: 'TransactionStatusCode',
      currency: 'CurrencyCode',
      description: 'Description',
      createdBy: 'CreatedByName',
      modifiedBy: 'ModifiedByName',
      accountCode: 'AccountCode',
      netAmount: 'DisplayNetAmount',
      vatAmount: 'DisplayVatAmount',
      onDispute: 'OnDispute',
      subLedger: 'SubLedger',
      orderNumber: 'OrderNumber',
      companyName: 'CompanyName',
      flags: 'Flags',
    }),
    [],
  );

  // Build visible columns for outstanding tab export
  const getOutstandingExportColumns = useCallback((): ColumnInput[] => {
    return outstandingCols
      .filter((col) => col.visible && col.id in transactionExportColumnNameMap)
      .map((col) => ({
        name: transactionExportColumnNameMap[col.id],
        displayName: col.label,
      }));
  }, [outstandingCols, transactionExportColumnNameMap]);

  // Build all columns for outstanding tab export
  const getOutstandingAllExportColumns = useCallback((): ColumnInput[] => {
    return DEFAULT_CUSTOMER_DETAIL_TX_COLUMNS.filter((col) => col.id in transactionExportColumnNameMap).map((col) => ({
      name: transactionExportColumnNameMap[col.id],
      displayName: col.label,
    }));
  }, [transactionExportColumnNameMap]);

  // Build visible columns for history tab export
  const getHistoryExportColumns = useCallback((): ColumnInput[] => {
    return historyCols
      .filter((col) => col.visible && col.id in transactionExportColumnNameMap)
      .map((col) => ({
        name: transactionExportColumnNameMap[col.id],
        displayName: col.label,
      }));
  }, [historyCols, transactionExportColumnNameMap]);

  // Build all columns for history tab export
  const getHistoryAllExportColumns = useCallback((): ColumnInput[] => {
    return DEFAULT_CUSTOMER_DETAIL_TX_COLUMNS.filter((col) => col.id in transactionExportColumnNameMap).map((col) => ({
      name: transactionExportColumnNameMap[col.id],
      displayName: col.label,
    }));
  }, [transactionExportColumnNameMap]);

  // Build filter for outstanding transactions export
  const getOutstandingExportFilter = useCallback((): Record<string, unknown> | undefined => {
    const filter: Record<string, unknown> = {};
    if (customerId) filter.customerId = { eq: Number(customerId) };
    filter.outstandingAmount = { ne: 0 };
    if (outstandingSearchQuery) filter.summary = outstandingSearchQuery;
    if (isConsolidatedView) filter.allCompanies = true;
    return Object.keys(filter).length > 0 ? filter : undefined;
  }, [customerId, outstandingSearchQuery, isConsolidatedView]);

  // Build filter for history transactions export
  const getHistoryExportFilter = useCallback((): Record<string, unknown> | undefined => {
    const filter: Record<string, unknown> = {};
    if (customerId) filter.customerId = { eq: Number(customerId) };
    filter.outstandingAmount = { eq: 0 };
    if (historySearchQuery) filter.summary = historySearchQuery;
    if (isConsolidatedView) filter.allCompanies = true;
    return Object.keys(filter).length > 0 ? filter : undefined;
  }, [customerId, historySearchQuery, isConsolidatedView]);

  // Server-side export hooks — Outstanding tab
  const { triggerExportAll: triggerOutstandingCurrentView } = useExportAll({
    form: 'Transactions',
    getColumns: getOutstandingExportColumns,
    getFilter: getOutstandingExportFilter,
    fileName: `outstanding-transactions-${customer?.name || 'customer'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const { triggerExportAll: triggerOutstandingAllColumns } = useExportAll({
    form: 'Transactions',
    getColumns: getOutstandingAllExportColumns,
    getFilter: getOutstandingExportFilter,
    fileName: `outstanding-transactions-all-columns-${customer?.name || 'customer'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  // Server-side export hooks — History tab
  const { triggerExportAll: triggerHistoryCurrentView } = useExportAll({
    form: 'Transactions',
    getColumns: getHistoryExportColumns,
    getFilter: getHistoryExportFilter,
    fileName: `history-transactions-${customer?.name || 'customer'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const { triggerExportAll: triggerHistoryAllColumns } = useExportAll({
    form: 'Transactions',
    getColumns: getHistoryAllExportColumns,
    getFilter: getHistoryExportFilter,
    fileName: `history-transactions-all-columns-${customer?.name || 'customer'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  // Server-side export hooks — Selection toolbar (outstanding)
  const getOutstandingSelectedExportFilter = useCallback((): Record<string, unknown> | undefined => {
    if (selectedRows.size === 0) return undefined;
    const filter: Record<string, unknown> = {};
    filter.transactionId = { containsValues: Array.from(selectedRows) };
    if (isConsolidatedView) filter.allCompanies = true;
    return filter;
  }, [selectedRows, isConsolidatedView]);

  const { triggerExportAll: triggerOutstandingSelectedCurrentView } = useExportAll({
    form: 'Transactions',
    getColumns: getOutstandingExportColumns,
    getFilter: getOutstandingSelectedExportFilter,
    fileName: `selected-outstanding-transactions-${customer?.name || 'customer'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const { triggerExportAll: triggerOutstandingSelectedAllColumns } = useExportAll({
    form: 'Transactions',
    getColumns: getOutstandingAllExportColumns,
    getFilter: getOutstandingSelectedExportFilter,
    fileName: `selected-outstanding-transactions-all-columns-${customer?.name || 'customer'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  // Server-side export hooks — Selection toolbar (history)
  const getHistorySelectedExportFilter = useCallback((): Record<string, unknown> | undefined => {
    if (historySelectedRows.size === 0) return undefined;
    const filter: Record<string, unknown> = {};
    filter.transactionId = { containsValues: Array.from(historySelectedRows) };
    if (isConsolidatedView) filter.allCompanies = true;
    return filter;
  }, [historySelectedRows, isConsolidatedView]);

  const { triggerExportAll: triggerHistorySelectedCurrentView } = useExportAll({
    form: 'Transactions',
    getColumns: getHistoryExportColumns,
    getFilter: getHistorySelectedExportFilter,
    fileName: `selected-history-transactions-${customer?.name || 'customer'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const { triggerExportAll: triggerHistorySelectedAllColumns } = useExportAll({
    form: 'Transactions',
    getColumns: getHistoryAllExportColumns,
    getFilter: getHistorySelectedExportFilter,
    fileName: `selected-history-transactions-all-columns-${customer?.name || 'customer'}-${new Date().toISOString().split('T')[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  const activeStrategies = useMemo(() => strategies.filter((x) => x.status === 'Active'), [strategies]);
  const statementTemplates = useMemo(() => templates.filter((x) => x.type === 'statement'), [templates]);
  const activeUsers = users;

  useEffect(() => {
    try {
      if (customer) {
        const strategyId = customer.collectionStrategyId || 'none';
        const hasStrategy = strategyId === 'none' || activeStrategies.some((x) => x.id === strategyId);
        setEditedCollectionStrategyId(hasStrategy ? strategyId : 'none');

        // The stored id is the group-level template; this company may see its override of it.
        const assignedTemplate = findTemplateByStoredId(statementTemplates, customer.statementTemplateId);
        setEditedStatementTemplateId(assignedTemplate ? logicalTemplateId(assignedTemplate) : 'none');

        const riskId = customer.riskLevelId ? String(customer.riskLevelId) : 'none';
        const hasRisk = riskId === 'none' || riskLevels.some((x) => String(x.id) === riskId);
        setEditedRiskLevelId(hasRisk ? riskId : 'none');

        const controllerId = customer.creditControllerId || 'none';
        const hasController = controllerId === 'none' || activeUsers.some((x) => x.id === controllerId);
        setEditedCreditControllerId(hasController ? controllerId : 'none');

        const managerId = customer.accountManagerId || 'none';
        const hasManager = managerId === 'none' || activeUsers.some((x) => x.id === managerId);
        setEditedAccountManagerId(hasManager ? managerId : 'none');

        setOnStopValue(customer.onStop ?? false);
      }
    } catch (error) {
      console.error('Error setting collection strategy:', error);
    }
  }, [customer, activeStrategies, statementTemplates, activeUsers, riskLevels]);

  // Clear selection, search and reset page when switching tabs
  useEffect(() => {
    setSelectedRows(new Set());
    setHistorySelectedRows(new Set());
    setOutstandingSearchQuery('');
    setHistorySearchQuery('');
    setOutstandingCurrentPage(1);
    setHistoryCurrentPage(1);
  }, [activeTab, activeTransactionTab]);

  // Reset pagination and selection when the any-currency filter changes
  useEffect(() => {
    setSelectedRows(new Set());
    setHistorySelectedRows(new Set());
    setOutstandingCurrentPage(1);
    setHistoryCurrentPage(1);
  }, [selectedAnyCurrency]);

  // Tabs hidden when consolidated view is active
  const hiddenTabsInConsolidated = new Set([
    'overview',
    'financial',
    'ageing',
    'communications',
    'notes',
    'contacts',
    'addresses',
    'audit',
  ]);

  // Reset pagination, selections, search, and switch tab when toggling consolidated view
  useEffect(() => {
    setSelectedRows(new Set());
    setHistorySelectedRows(new Set());
    setOutstandingCurrentPage(1);
    setHistoryCurrentPage(1);
    setOutstandingSearchQuery('');
    setHistorySearchQuery('');
    if (isConsolidatedView && hiddenTabsInConsolidated.has(activeTab)) {
      setActiveTab(TRANSACTIONS_TAB);
    }
  }, [isConsolidatedView]);

  const error = customerError
    ? customerError instanceof Error
      ? customerError.message
      : 'Failed to load customer'
    : null;

  // Server-side search is used — no client-side filtering needed.
  // Transactions are already filtered by the API via the search param.
  const filteredOutstandingTransactions = outstandingTransactions;
  const filteredCompletedTransactions = completedTransactions;

  // Selection handlers for Outstanding tab
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(new Set(filteredOutstandingTransactions.map((t) => t.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id: number, checked: boolean) => {
    const newSelected = new Set(selectedRows);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedRows(newSelected);
  };

  // Get selected transactions for Outstanding tab
  const selectedTransactions = outstandingTransactions.filter((t) => selectedRows.has(t.id));

  // Calculate totals for selected transactions (Outstanding tab)
  const customerBaseCurrency = selectedCompany?.currency ?? 'GBP';
  const selectionTotals = calculateTransactionTotals(selectedTransactions, customerBaseCurrency);

  // Get selected transactions for History tab
  const historySelectedTransactions = completedTransactions.filter((t) => historySelectedRows.has(t.id));

  // Calculate totals for selected transactions (History tab)
  const historySelectionTotals = calculateTransactionTotals(historySelectedTransactions, customerBaseCurrency);

  const taskDrawerCustomer = {
    id: customerId ?? '',
    name: customer?.name || '',
    accountNumber: customer?.accountNumber || '',
  };

  // Action handlers
  const handleExportSelected = () => {
    triggerOutstandingSelectedCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  const handleOutstandingExportCurrentView = () => {
    triggerOutstandingCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  const handleOutstandingExportAllColumns = () => {
    triggerOutstandingAllColumns();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  const handleOutstandingSelectedExportCurrentView = () => {
    triggerOutstandingSelectedCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  const handleOutstandingSelectedExportAllColumns = () => {
    triggerOutstandingSelectedAllColumns();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  const handleSendReminders = (transactionIds: number[]) => {
    // In a real app, this would call an API
    toast.success(`Payment reminders sent for ${transactionIds.length} invoice(s)`);
    setSelectedRows(new Set());
    setSendRemindersDialogOpen(false);
  };

  // Handler for navigating to allocate screen when unallocated receipts are selected
  const handleNavigateToAllocate = () => {
    // Get only unallocated receipts from selection
    const unallocatedReceipts = selectedTransactions.filter(
      (t) => t.transactionType === 'receipt' && t.outstandingAmount !== undefined && t.outstandingAmount !== 0,
    );

    if (unallocatedReceipts.length === 0) {
      toast.error('No unallocated receipts selected');
      return;
    }

    // If only one receipt is selected, navigate directly to its allocate page
    if (unallocatedReceipts.length === 1) {
      navigate(`/payments/allocate/${unallocatedReceipts[0].id}`);
    } else {
      // For multiple receipts, navigate to the first one
      // In a real app, you might want to show a selection dialog or navigate to a list view
      navigate(`/payments/allocate/${unallocatedReceipts[0].id}`);
      toast.info(`Navigating to allocate screen for receipt ${unallocatedReceipts[0].referenceNumber}`);
    }
  };

  const handleClearSelection = () => {
    setSelectedRows(new Set());
  };

  const handleSendCopyInvoiceSuccess = () => {
    setSelectedRows(new Set());
    setSendCopyInvoiceDialogOpen(false);
  };

  const handleCreateCollectionCase = () => {
    // Called when case is created successfully from the drawer
    setCreateCollectionCaseModalOpen(false);
    setSelectedRows(new Set()); // Clear selection
  };

  const handleCreatePromise = (data: PromiseFormData) => {
    // In a real app, this would call an API
    const invoiceCount = data.selectedInvoices.length;
    toast.success(`Promise to pay recorded for ${invoiceCount} transaction(s)`);
    setCreatePromiseModalOpen(false);
    setSelectedRows(new Set()); // Clear selection
  };

  const handleCreateDispute = (data: DisputeFormData) => {
    setCreateDisputeModalOpen(false);
    setSelectedRows(new Set()); // Clear selection
  };

  const handleAddNote = (data: NoteFormData) => {
    // In a real app, this would call an API
    const noteCount = data.customerIds.length;
    toast.success(`Note${noteCount > 1 ? 's' : ''} added to ${noteCount} customer${noteCount > 1 ? 's' : ''}`);
    setAddNoteModalOpen(false);
    setSelectedRows(new Set()); // Clear selection
  };

  // Selection handlers for History tab
  const handleHistorySelectAll = (checked: boolean) => {
    if (checked) {
      setHistorySelectedRows(new Set(filteredCompletedTransactions.map((t) => t.id)));
    } else {
      setHistorySelectedRows(new Set());
    }
  };

  const handleHistorySelectRow = (id: number, checked: boolean) => {
    const newSelected = new Set(historySelectedRows);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setHistorySelectedRows(newSelected);
  };

  // Action handlers for History tab
  const handleHistoryExportSelected = () => {
    triggerHistorySelectedCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  const handleHistoryExportCurrentView = () => {
    triggerHistoryCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  const handleHistoryExportAllColumns = () => {
    triggerHistoryAllColumns();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  const handleHistorySelectedExportCurrentView = () => {
    triggerHistorySelectedCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  const handleHistorySelectedExportAllColumns = () => {
    triggerHistorySelectedAllColumns();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  const handleHistorySendReminders = (transactionIds: number[]) => {
    // In a real app, this would call an API
    toast.success(`Payment reminders sent for ${transactionIds.length} invoice(s)`);
    setHistorySelectedRows(new Set());
    setHistorySendRemindersDialogOpen(false);
  };

  // Handler for navigating to allocate screen from history tab
  const handleHistoryNavigateToAllocate = () => {
    // Get only unallocated receipts from selection
    const unallocatedReceipts = historySelectedTransactions.filter(
      (t) => t.transactionType === 'receipt' && t.outstandingAmount !== undefined && t.outstandingAmount !== 0,
    );

    if (unallocatedReceipts.length === 0) {
      toast.error('No unallocated receipts selected');
      return;
    }

    // If only one receipt is selected, navigate directly to its allocate page
    if (unallocatedReceipts.length === 1) {
      navigate(`/payments/allocate/${unallocatedReceipts[0].id}`);
    } else {
      // For multiple receipts, navigate to the first one
      // In a real app, you might want to show a selection dialog or navigate to a list view
      navigate(`/payments/allocate/${unallocatedReceipts[0].id}`);
      toast.info(`Navigating to allocate screen for receipt ${unallocatedReceipts[0].referenceNumber}`);
    }
  };

  const handleHistoryClearSelection = () => {
    setHistorySelectedRows(new Set());
  };

  const handleHistorySendCopyInvoiceSuccess = () => {
    setHistorySelectedRows(new Set());
    setHistorySendCopyInvoiceDialogOpen(false);
  };

  const handleHistoryCreateCollectionCase = () => {
    // Called when case is created successfully from the drawer
    setHistoryCreateCollectionCaseModalOpen(false);
    setHistorySelectedRows(new Set()); // Clear selection
  };

  const handleHistoryCreatePromise = (data: PromiseFormData) => {
    // In a real app, this would call an API
    const invoiceCount = data.selectedInvoices.length;
    toast.success(`Promise to pay recorded for ${invoiceCount} transaction(s)`);
    setHistoryCreatePromiseModalOpen(false);
    setHistorySelectedRows(new Set()); // Clear selection
  };

  const handleHistoryCreateDispute = (data: DisputeFormData) => {
    setHistoryCreateDisputeModalOpen(false);
    setHistorySelectedRows(new Set()); // Clear selection
  };

  const handleHistoryAddNote = (data: NoteFormData) => {
    // In a real app, this would call an API
    const noteCount = data.customerIds.length;
    toast.success(`Note${noteCount > 1 ? 's' : ''} added to ${noteCount} customer${noteCount > 1 ? 's' : ''}`);
    setHistoryAddNoteModalOpen(false);
    setHistorySelectedRows(new Set()); // Clear selection
  };

  // Risk badge helper
  const getRiskBadge = (risk: string) => {
    return (
      <Badge className={getRiskBadgeClassName(risk as RiskLevelCode)}>{getRiskLabel(risk as RiskLevelCode)}</Badge>
    );
  };

  // Derive the effective risk code from the edited risk level dropdown,
  // so the badge updates immediately after saving without waiting for a refetch.
  const effectiveRiskCode = useMemo(() => {
    if (editedRiskLevelId && editedRiskLevelId !== 'none') {
      const selectedLevel = riskLevels.find((l) => String(l.id) === editedRiskLevelId);
      if (selectedLevel) {
        return selectedLevel.code.toLowerCase();
      }
    }
    return editedRiskLevelId === 'none' ? 'none' : (customer?.risk ?? 'none');
  }, [editedRiskLevelId, riskLevels, customer?.risk]);

  const hasPrimaryContact = customer?.hasPrimaryContact !== false;
  const contactWarning = primaryContactWarning(customer);

  const handleSaveCollectionSettings = async () => {
    if (!customer || !customerId) return;

    // A strategy cannot run without someone to run it for, so the pairing is enforced on save
    if (editedCollectionStrategyId !== 'none' && editedCreditControllerId === 'none') {
      toast.error('You must assign a credit controller when assigning a strategy');
      return;
    }

    // Only warn when the strategy itself is being changed, not on every settings save
    const strategyChanged = editedCollectionStrategyId !== (customer.collectionStrategyId || 'none');
    const selectedStrategy = strategies.find((x) => x.id === editedCollectionStrategyId);
    if (strategyChanged && !hasPrimaryContact && strategyHasEmailAction(selectedStrategy)) {
      setStrategyEmailWarningOpen(true);
      return;
    }

    await saveCollectionSettings();
  };

  const saveCollectionSettings = async () => {
    if (!customer || !customerId) return;

    try {
      if (mode === 'live') {
        await apiPut(`/api/v1/customers/${customerId}`, {
          collectionStrategyId: editedCollectionStrategyId === 'none' ? null : Number(editedCollectionStrategyId),
          statementTemplateId: editedStatementTemplateId === 'none' ? null : Number(editedStatementTemplateId),
          riskLevelId: editedRiskLevelId === 'none' ? null : Number(editedRiskLevelId),
          creditControllerId: editedCreditControllerId === 'none' ? null : Number(editedCreditControllerId),
          accountManagerId: editedAccountManagerId === 'none' ? null : Number(editedAccountManagerId),
        });
        await queryClient.invalidateQueries({ queryKey: ['customer-detail', customerId] });
        await queryClient.invalidateQueries({ queryKey: ['customer-audit-logs', customerId] });
      } else {
        applyCollectionSettingsToMockCustomer(customerId, {
          collectionStrategyId: editedCollectionStrategyId,
          statementTemplateSelection: editedStatementTemplateId,
          riskCode: effectiveRiskCode,
          creditControllerId: editedCreditControllerId,
          accountManagerId: editedAccountManagerId,
        });
        await queryClient.invalidateQueries({ queryKey: ['customer-detail', customerId] });
      }
      await invalidateCustomerListQueries(queryClient);
      toast.success('Collection settings updated successfully');
    } catch (error) {
      console.error('Error saving collection settings:', error);
      toast.error('Failed to save collection settings');
    }
  };

  // Handle On Stop toggle
  const handleOnStopChange = async (checked: boolean) => {
    if (!customer || !customerId) return;

    // If enabling On Stop and notes are required by settings, show the dialog
    if (checked && generalSettings?.onStopNotesEnabled) {
      setPendingOnStopValue(checked);
      setOnStopNoteDialogOpen(true);
      return;
    }

    // Otherwise proceed with the update directly
    await updateOnStopStatus(checked);
  };

  // Update On Stop status (with optional note)
  const updateOnStopStatus = async (checked: boolean, note?: string) => {
    if (!customer || !customerId) return;

    setOnStopValue(checked);

    try {
      if (mode === 'live') {
        // In live mode, call the API to update the customer
        await apiPut(`/api/v1/customers/${customerId}`, {
          onStop: checked,
        });
        await invalidateCustomerListQueries(queryClient);
        toast.success(`On Stop status updated to ${checked ? 'Yes' : 'No'}`);
      } else {
        // In demo mode, just show success message
        toast.success(`On Stop status updated to ${checked ? 'Yes' : 'No'}`);
      }

      // If a note was provided, save it
      if (note && checked) {
        await addNoteMutation.mutateAsync({
          customerId,
          content: note,
          userId: currentUser.id,
          userName: `${currentUser.firstName} ${currentUser.lastName}`,
        });
      }

      // Invalidate audit logs so the audit tab shows new entries
      await queryClient.invalidateQueries({ queryKey: ['customer-audit-logs', customerId] });
    } catch (error) {
      console.error('Error updating On Stop status:', error);
      toast.error('Failed to update On Stop status');
      // Revert the change on error
      setOnStopValue(!checked);
    }
  };

  // Handle saving the On Stop note
  const handleSaveOnStopNote = async (note: string) => {
    if (pendingOnStopValue !== null) {
      await updateOnStopStatus(pendingOnStopValue, note);
      setPendingOnStopValue(null);
    }
  };

  // Calculate ageing based on outstanding transactions using dynamic bands
  const calculateAgeing = () => {
    if (!ageingBands) return null;
    const today = new Date();
    const bandTotals = new Map<number, number>();

    for (const band of ageingBands) {
      bandTotals.set(band.sequence, 0);
    }

    outstandingTransactions.forEach((transaction) => {
      if (!transaction.outstandingAmount || transaction.outstandingAmount <= 0) return;
      if (!transaction.dueDate) return;

      const dueDate = new Date(transaction.dueDate);
      const daysOld = Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
      const amount = transaction.outstandingAmount;

      for (const band of ageingBands) {
        if (daysOld >= band.startDays && (band.endDays === null || daysOld <= band.endDays)) {
          bandTotals.set(band.sequence, (bandTotals.get(band.sequence) ?? 0) + amount);
          break;
        }
      }
    });

    const bands = ageingBands.map((x) => ({
      sequence: x.sequence,
      label: x.label,
      bandLabel: x.bandLabel,
      startDays: x.startDays,
      endDays: x.endDays,
      totalOutstanding: bandTotals.get(x.sequence) ?? 0,
      color: x.color,
    }));

    const total = bands.reduce((acc, x) => acc + x.totalOutstanding, 0);
    const overdue = bands.filter((x) => x.startDays > 0).reduce((acc, x) => acc + x.totalOutstanding, 0);

    return { bands, total, overdue, unallocatedReceipts: 0 };
  };

  // Toggle payment expansion (kept for potential future use)
  const togglePaymentExpansion = (paymentId: string) => {
    if (expandedPayments.includes(paymentId)) {
      setExpandedPayments(expandedPayments.filter((id) => id !== paymentId));
    } else {
      setExpandedPayments([...expandedPayments, paymentId]);
    }
  };
  // Mark functions as intentionally kept for future use
  void togglePaymentExpansion;
  void calculateAgeing;

  console.log('Rendering CustomerDetailPage - customer state:', customer);
  console.log('isLoading:', isLoading, 'error:', error);

  // Show loading state
  const locationState = location.state as { customer?: { name?: string; accountNumber?: string } } | null;
  if (isLoading) {
    return (
      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-lg text-muted-foreground">Loading customer details...</p>
            {locationState?.customer?.accountNumber && (
              <p className="text-sm text-muted-foreground mt-2">
                Loading customer {locationState.customer.accountNumber}...
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-red-600 mb-2">Error Loading Customer</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate('/customers')}>Back to Customers</Button>
          </div>
        </div>
      </div>
    );
  }

  // Show not found state
  if (!customer) {
    console.log('Customer is null - showing not found message');
    return (
      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">Customer Not Found</h2>
            <p className="text-muted-foreground mb-2">
              The customer you're looking for doesn't exist or couldn't be loaded.
            </p>
            <p className="text-sm text-muted-foreground mb-4">The requested customer could not be found.</p>
            <Button onClick={() => navigate('/customers')}>Back to Customers</Button>
          </div>
        </div>
      </div>
    );
  }

  console.log('Customer found - rendering detail page for:', customer.name);

  // Main render - wrapped in try-catch for safety
  try {
    return (
      <div className="flex flex-col gap-6 p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">{customer.name}</h1>
              {getRiskBadge(effectiveRiskCode)}
              {isMultiCompanyGroup && (
                <div className="flex items-center gap-2 ml-2">
                  <Label htmlFor="consolidated-view" className="text-sm font-medium cursor-pointer">
                    Consolidated View
                  </Label>
                  <Switch id="consolidated-view" checked={isConsolidatedView} onCheckedChange={setIsConsolidatedView} />
                </div>
              )}
            </div>
            <p className="text-muted-foreground">{customer.accountNumber}</p>
          </div>
          <div className="flex items-center gap-4">
            {customer.isAnyCurrency ? (
              /* Any Currency account — show a dropdown of all currencies the
                 account has transacted in. Selecting GBP (base) shows all
                 transactions; any other currency filters to that currency. */
              <Select value={selectedAnyCurrency} onValueChange={setSelectedAnyCurrency}>
                <SelectTrigger className="w-auto min-w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={selectedCompany?.currency ?? 'GBP'}>
                    {selectedCompany?.currency ?? 'GBP'} (Base — All)
                  </SelectItem>
                  {(customer.transactionCurrencies ?? [])
                    .filter((c) => c !== (selectedCompany?.currency ?? 'GBP'))
                    .map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            ) : (
              <CurrencySelect
                customerCurrency={customer.currency}
                baseCurrency={selectedCompany?.currency ?? customer.currency}
                value={detailCurrencyMode}
                onChange={setDetailCurrencyMode}
              />
            )}
            <Button variant="outline" onClick={() => navigate('/customers')}>
              Back to Customers
            </Button>
            <HelpSupportButton screenId="customer-detail" />
          </div>
        </div>

        {/* Metric Cards — always show group-level totals */}
        <CustomerMetricCards
          customer={customer}
          payments={payments.map((p) => ({
            id: p.id,
            date: p.date,
            amount: p.amount,
            invoiceDate: p.allocations[0]?.invoiceDate,
          }))}
          isConsolidatedView={isConsolidatedView}
          isMultiCompanyGroup={isMultiCompanyGroup}
          detailCurrencyMode={detailCurrencyMode}
          selectedCurrency={customer.isAnyCurrency ? selectedAnyCurrency : undefined}
          baseCurrency={selectedCompany?.currency ?? 'GBP'}
          onCardClick={(action) => {
            switch (action) {
              case 'outstanding':
              case 'overdue':
                setActiveTab(TRANSACTIONS_TAB);
                setActiveTransactionTab('outstanding');
                break;
              case 'credit':
                if (!isConsolidatedView) {
                  setActiveTab('financial');
                }
                break;
              case 'performance':
                setActiveTab(TRANSACTIONS_TAB);
                setActiveTransactionTab('payment-history');
                break;
            }
          }}
        />

        {/* Customer Details Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className={cn('grid w-full', isConsolidatedView ? 'grid-cols-5' : 'grid-cols-13')}>
            {!isConsolidatedView && <TabsTrigger value="overview">Overview</TabsTrigger>}
            {!isConsolidatedView && <TabsTrigger value="financial">Financial</TabsTrigger>}
            {!isConsolidatedView && <TabsTrigger value="ageing">Ageing</TabsTrigger>}
            <TabsTrigger value={TRANSACTIONS_TAB}>Transactions</TabsTrigger>
            <TabsTrigger value="collections">Collections</TabsTrigger>
            <TabsTrigger value="promises">Promises</TabsTrigger>
            <TabsTrigger value="disputes">Disputes</TabsTrigger>
            {!isConsolidatedView && <TabsTrigger value="communications">Communications</TabsTrigger>}
            {!isConsolidatedView && <TabsTrigger value="notes">Notes</TabsTrigger>}
            {!isConsolidatedView && <TabsTrigger value="contacts">Contacts</TabsTrigger>}
            {!isConsolidatedView && <TabsTrigger value="addresses">Addresses</TabsTrigger>}
            {isDemo && <TabsTrigger value="documents">Documents</TabsTrigger>}
            {!isConsolidatedView && <TabsTrigger value="audit">Audit Log</TabsTrigger>}
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            {contactWarning && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{contactWarning}</AlertDescription>
              </Alert>
            )}
            <div className="grid gap-4 lg:grid-cols-[1.1fr_1fr] items-stretch">
              {/* Left Column - Recent Activity */}
              <div>
                <RecentActivityPanel customerId={customer.id} />
              </div>

              {/* Right Column - Existing Info Panels */}
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-primary" />
                      Account Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-x-6">
                      <div className="space-y-3">
                        <div>
                          <Label className="text-muted-foreground">Account Number</Label>
                          <p className="font-medium">{customer.accountNumber}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Customer Name</Label>
                          <p className="font-medium">{customer.name}</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-muted-foreground">Currency</Label>
                          <p className="font-medium">{customer.currency}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">On Stop</Label>
                          <div className="mt-1 flex items-center gap-2">
                            <Switch checked={onStopValue} onCheckedChange={handleOnStopChange} />
                            <span className="text-sm font-medium">{onStopValue ? 'Yes' : 'No'}</span>
                          </div>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Status</Label>
                          <div className="mt-1">
                            {customer.status ? (
                              <Badge
                                className={
                                  customer.status === 'Active'
                                    ? 'bg-green-600 text-white hover:bg-green-700'
                                    : 'bg-gray-500 text-white hover:bg-gray-600'
                                }
                              >
                                {customer.status}
                              </Badge>
                            ) : (
                              <span className="text-sm text-muted-foreground">Not Available</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Phone className="h-5 w-5 text-primary" />
                      Contact Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-muted-foreground">Email</Label>
                      <p className="font-medium">{customer.contact}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Phone</Label>
                      <p className="font-medium">{customer.phone}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Address</Label>
                      <p className="font-medium">{customer.address}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-primary" />
                  Collection Settings
                  <TooltipProvider delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0" />
                      </TooltipTrigger>
                      <TooltipContent side="right">
                        <p>
                          For a collection strategy to run for this customer, you must also assign a credit controller
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-end gap-4">
                  <div className="flex-1">
                    <Label>Assigned Strategy</Label>
                    <Select value={editedCollectionStrategyId} onValueChange={setEditedCollectionStrategyId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select strategy" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {activeStrategies.map((strategy) => (
                          <SelectItem key={strategy.id} value={strategy.id}>
                            {strategy.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label>Statement Template</Label>
                    <Select value={editedStatementTemplateId} onValueChange={setEditedStatementTemplateId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select statement template" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {statementTemplates.map((template) => (
                          <SelectItem key={template.id} value={logicalTemplateId(template)}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label>Risk Level</Label>
                    <Select value={editedRiskLevelId} onValueChange={setEditedRiskLevelId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select risk level" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {riskLevels.map((level) => (
                          <SelectItem key={level.id} value={String(level.id)}>
                            {level.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label>Assigned Credit Controller</Label>
                    <Select value={editedCreditControllerId} onValueChange={setEditedCreditControllerId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select user">
                          {editedCreditControllerId && editedCreditControllerId !== 'none' && (
                            <UserAvatar
                              user={activeUsers.find((x) => x.id === editedCreditControllerId)!}
                              size="sm"
                              showName
                            />
                          )}
                          {editedCreditControllerId === 'none' && 'None'}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {activeUsers.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            <UserAvatar user={user} size="sm" showName />
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label>Account Manager</Label>
                    <Select value={editedAccountManagerId} onValueChange={setEditedAccountManagerId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select user">
                          {editedAccountManagerId && editedAccountManagerId !== 'none' && (
                            <UserAvatar
                              user={activeUsers.find((x) => x.id === editedAccountManagerId)!}
                              size="sm"
                              showName
                            />
                          )}
                          {editedAccountManagerId === 'none' && 'None'}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {activeUsers.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            <UserAvatar user={user} size="sm" showName />
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    onClick={handleSaveCollectionSettings}
                    disabled={
                      editedCollectionStrategyId === (customer.collectionStrategyId || 'none') &&
                      editedStatementTemplateId === (customer.statementTemplateId || 'none') &&
                      editedRiskLevelId === (customer.riskLevelId ? String(customer.riskLevelId) : 'none') &&
                      editedCreditControllerId === (customer.creditControllerId || 'none') &&
                      editedAccountManagerId === (customer.accountManagerId || 'none')
                    }
                  >
                    Save
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tag className="h-5 w-5 text-primary" />
                  Category Keys
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-x-6">
                  <div>
                    <Label className="text-muted-foreground">
                      {generalSettings?.categoryKeys?.userSortKey1Label ?? 'User Sort Key 1'}
                    </Label>
                    <p className="font-medium">
                      {customer.userSortKey1 || <span className="text-sm text-muted-foreground">Not Available</span>}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">
                      {generalSettings?.categoryKeys?.userSortKey2Label ?? 'User Sort Key 2'}
                    </Label>
                    <p className="font-medium">
                      {customer.userSortKey2 || <span className="text-sm text-muted-foreground">Not Available</span>}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">
                      {generalSettings?.categoryKeys?.userSortKey3Label ?? 'User Sort Key 3'}
                    </Label>
                    <p className="font-medium">
                      {customer.userSortKey3 || <span className="text-sm text-muted-foreground">Not Available</span>}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">
                      {generalSettings?.categoryKeys?.userSortKey4Label ?? 'User Sort Key 4'}
                    </Label>
                    <p className="font-medium">
                      {customer.userSortKey4 || <span className="text-sm text-muted-foreground">Not Available</span>}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Financial Tab */}
          <TabsContent value="financial" className="space-y-4">
            {(() => {
              const base = selectedCompany?.currency ?? 'GBP';
              const isNonBaseCurrencyActive = customer.isAnyCurrency
                ? selectedAnyCurrency !== base
                : detailCurrencyMode !== 'base' && customer.currency !== base;

              const financialCreditLimit = isNonBaseCurrencyActive
                ? (customer.currencyCreditLimit ?? customer.creditLimit)
                : customer.creditLimit;
              const financialOutstanding = isNonBaseCurrencyActive
                ? financialMetricTotals.currencyOutstanding
                : financialMetricTotals.baseOutstanding;
              const financialAvailable = financialCreditLimit - financialOutstanding;
              const financialUtilisation =
                financialCreditLimit > 0 ? (financialOutstanding / financialCreditLimit) * 100 : 0;
              const displayCurrency = isNonBaseCurrencyActive
                ? customer.isAnyCurrency
                  ? selectedAnyCurrency
                  : customer.currency
                : base;
              return (
                <div className="grid gap-4 md:grid-cols-3">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Credit Limit</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold">{formatCurrency(financialCreditLimit, displayCurrency)}</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Available Credit</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {financialCreditLimit === 0 ? (
                        <p className="text-lg font-medium text-muted-foreground">No Credit Limit Set</p>
                      ) : (
                        <p className="text-3xl font-bold text-green-600">
                          {formatCurrency(financialAvailable, displayCurrency)}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Credit Utilisation</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {financialCreditLimit === 0 ? (
                        <p className="text-lg font-medium text-muted-foreground">No Credit Limit Set</p>
                      ) : (
                        <>
                          <div className="flex justify-between text-sm">
                            <span>
                              {formatCurrency(financialOutstanding, displayCurrency)} of{' '}
                              {formatCurrency(financialCreditLimit, displayCurrency)}
                            </span>
                            <span className="font-medium">{Math.round(financialUtilisation)}%</span>
                          </div>
                          <Progress value={financialUtilisation} />
                        </>
                      )}
                    </CardContent>
                  </Card>
                </div>
              );
            })()}

            <Card>
              <CardHeader className="pb-2">
                <CardTitle>Payment Terms & Performance</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <Label className="text-muted-foreground">Payment Terms</Label>
                    <p className="text-lg font-medium">{customer.paymentTerms}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Due Days</Label>
                    <p className="text-lg font-medium">
                      {customer.dueDays !== undefined ? (
                        customer.dueDays
                      ) : (
                        <span className="text-sm text-muted-foreground">Not Available</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Due Days Type</Label>
                    <p className="text-lg font-medium">
                      {customer.dueDaysType || <span className="text-sm text-muted-foreground">Not Available</span>}
                    </p>
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <Label className="text-muted-foreground">Payment Performance</Label>
                    <p className="text-lg font-medium">{customer.groupDso ?? customer.dso} days</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Risk Level</Label>
                    <div className="mt-1">{getRiskBadge(effectiveRiskCode)}</div>
                  </div>
                </div>

                {/* Credit Limit fields — read-only, synced from Finance system */}
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-1">
                    <Label className="text-muted-foreground">
                      Credit Limit
                      {selectedCompany?.currency && <span className="ml-1 text-xs">({selectedCompany.currency})</span>}
                    </Label>
                    <p className="text-lg font-medium">
                      {formatCurrency(customer.creditLimit, selectedCompany?.currency ?? customer.currency)}
                    </p>
                  </div>

                  {/* Currency Credit Limit — only shown when customer currency differs from base currency */}
                  {customer.currency && selectedCompany?.currency && customer.currency !== selectedCompany.currency && (
                    <div className="space-y-1">
                      <Label className="text-muted-foreground">
                        Currency Credit Limit <span className="text-xs">({customer.currency})</span>
                      </Label>
                      <p className="text-lg font-medium">
                        {formatCurrency(customer.currencyCreditLimit, customer.currency)}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ageing Tab */}
          <TabsContent value="ageing" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-base">
                  Ageing Summary
                  <ScopeLabel isMultiCompanyGroup={isMultiCompanyGroup} isConsolidatedView={isConsolidatedView} />
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-2 pb-3">
                {(customerAgeing ?? calculateAgeing()) && (
                  <AgeingBarChart
                    data={(customerAgeing ?? calculateAgeing())!}
                    currency={ageingCurrency ?? selectedCompany?.currency}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value={TRANSACTIONS_TAB} className="space-y-4">
            <Tabs value={activeTransactionTab} onValueChange={setActiveTransactionTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="outstanding">Outstanding</TabsTrigger>
                <TabsTrigger value="payment-history">History</TabsTrigger>
              </TabsList>

              {/* Outstanding Sub-Tab */}
              <TabsContent value="outstanding" className="space-y-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-base font-medium">
                      Outstanding Transactions
                      <ScopeLabel isMultiCompanyGroup={isMultiCompanyGroup} isConsolidatedView={isConsolidatedView} />
                      {outstandingSearchQuery && outstandingPagination && (
                        <span className="ml-2 text-sm font-normal text-muted-foreground">
                          ({outstandingPagination.totalItems} results)
                        </span>
                      )}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="text"
                          placeholder="Search transactions..."
                          value={outstandingSearchQuery}
                          onChange={(e) => {
                            setOutstandingSearchQuery(e.target.value);
                            setOutstandingCurrentPage(1);
                          }}
                          className="pl-8 pr-8 h-9 w-[250px]"
                        />
                        {outstandingSearchQuery && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setOutstandingSearchQuery('')}
                            className="absolute right-0 top-0 h-9 w-9 p-0 hover:bg-transparent"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={filteredOutstandingTransactions.length === 0}
                            className="h-9"
                            title="Export to Excel"
                          >
                            <ArrowDownToLine className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={handleOutstandingExportCurrentView}>
                            Export Current View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={handleOutstandingExportAllColumns}>
                            Export All Columns
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <ColumnConfigDialog
                        columns={outstandingCols}
                        onApply={saveOutstandingPrefs}
                        onReset={resetOutstandingPrefs}
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="pt-2">
                    {transactionsLoading ? (
                      <div className="flex items-center justify-center h-32">
                        <p className="text-muted-foreground">Loading transactions...</p>
                      </div>
                    ) : filteredOutstandingTransactions.length === 0 && outstandingSearchQuery ? (
                      <div className="flex flex-col items-center justify-center h-32 text-center">
                        <p className="text-muted-foreground">
                          No transactions found matching "{outstandingSearchQuery}"
                        </p>
                        <Button variant="link" onClick={() => setOutstandingSearchQuery('')} className="mt-2">
                          Clear search
                        </Button>
                      </div>
                    ) : (
                      <TransactionTable
                        transactions={filteredOutstandingTransactions}
                        columnVisibility={columnVisibility}
                        orderedColumns={outstandingCols}
                        onViewTransaction={(transaction) => {
                          setSelectedTransaction(transaction);
                          setDetailSheetOpen(true);
                        }}
                        showCustomerColumn={false}
                        enableSelection={!isConsolidatedView}
                        selectedRows={selectedRows}
                        onSelectRow={handleSelectRow}
                        onSelectAll={handleSelectAll}
                        currentPage={outstandingCurrentPage}
                        pageSize={outstandingPageSize}
                        onPageChange={setOutstandingCurrentPage}
                        onPageSizeChange={setOutstandingPageSize}
                        totalItems={outstandingPagination?.totalItems}
                        onSortChange={(sortBy, sortOrder) => {
                          setOutstandingSortBy(sortBy);
                          setOutstandingSortOrder(sortOrder);
                          setOutstandingCurrentPage(1);
                        }}
                        displayAmounts={true}
                      />
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Payment History Sub-Tab */}
              <TabsContent value="payment-history" className="space-y-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-base font-medium">
                      Transaction History
                      <ScopeLabel isMultiCompanyGroup={isMultiCompanyGroup} isConsolidatedView={isConsolidatedView} />
                      {historySearchQuery && historyPagination && (
                        <span className="ml-2 text-sm font-normal text-muted-foreground">
                          ({historyPagination.totalItems} results)
                        </span>
                      )}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="text"
                          placeholder="Search transactions..."
                          value={historySearchQuery}
                          onChange={(e) => {
                            setHistorySearchQuery(e.target.value);
                            setHistoryCurrentPage(1);
                          }}
                          className="pl-8 pr-8 h-9 w-[250px]"
                        />
                        {historySearchQuery && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setHistorySearchQuery('')}
                            className="absolute right-0 top-0 h-9 w-9 p-0 hover:bg-transparent"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={filteredCompletedTransactions.length === 0}
                            className="h-9"
                            title="Export to Excel"
                          >
                            <ArrowDownToLine className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={handleHistoryExportCurrentView}>
                            Export Current View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={handleHistoryExportAllColumns}>
                            Export All Columns
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <ColumnConfigDialog
                        columns={historyCols}
                        onApply={saveHistoryPrefs}
                        onReset={resetHistoryPrefs}
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="pt-2">
                    {completedTransactionsLoading ? (
                      <div className="flex items-center justify-center h-32">
                        <p className="text-muted-foreground">Loading completed transactions...</p>
                      </div>
                    ) : filteredCompletedTransactions.length === 0 && historySearchQuery ? (
                      <div className="flex flex-col items-center justify-center h-32 text-center">
                        <p className="text-muted-foreground">No transactions found matching "{historySearchQuery}"</p>
                        <Button variant="link" onClick={() => setHistorySearchQuery('')} className="mt-2">
                          Clear search
                        </Button>
                      </div>
                    ) : (
                      <TransactionTable
                        transactions={filteredCompletedTransactions}
                        columnVisibility={historyColumnVisibility}
                        orderedColumns={historyCols}
                        onViewTransaction={(transaction) => {
                          setSelectedTransaction(transaction);
                          setDetailSheetOpen(true);
                        }}
                        showCustomerColumn={false}
                        enableSelection={!isConsolidatedView}
                        selectedRows={historySelectedRows}
                        onSelectRow={handleHistorySelectRow}
                        onSelectAll={handleHistorySelectAll}
                        currentPage={historyCurrentPage}
                        pageSize={historyPageSize}
                        onPageChange={setHistoryCurrentPage}
                        onPageSizeChange={setHistoryPageSize}
                        totalItems={historyPagination?.totalItems}
                        onSortChange={(sortBy, sortOrder) => {
                          setHistorySortBy(sortBy);
                          setHistorySortOrder(sortOrder);
                          setHistoryCurrentPage(1);
                        }}
                        displayAmounts={true}
                      />
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Collections Tab */}
          <TabsContent value="collections">
            <CollectionsTab
              customerId={customer.id}
              customerName={customer.name}
              customerPhone={customer.phone}
              accountNumber={customer.accountNumber}
              isConsolidatedView={isConsolidatedView}
              isMultiCompanyGroup={isMultiCompanyGroup}
            />
          </TabsContent>

          {/* Promises Tab */}
          <TabsContent value="promises">
            <PromisesTab
              customerId={customer.id}
              customerName={customer.name}
              accountNumber={customer.accountNumber}
              isConsolidatedView={isConsolidatedView}
              isMultiCompanyGroup={isMultiCompanyGroup}
            />
          </TabsContent>

          {/* Disputes Tab */}
          <TabsContent value="disputes">
            <DisputesTab
              customerId={customer.id}
              customerName={customer.name}
              isConsolidatedView={isConsolidatedView}
              isMultiCompanyGroup={isMultiCompanyGroup}
            />
          </TabsContent>

          {/* Communications Tab */}
          <TabsContent value="communications">
            <CommunicationHistoryTab customerId={customer.id} customerName={customer.name} />
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            <AllContactsTab customerId={customer.id} customerName={customer.name} />
          </TabsContent>

          {/* Addresses Tab */}
          <TabsContent value="addresses">
            <AddressesTab customerId={customer.id} customerName={customer.name} primaryAddress={customer.address} />
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes">
            <NotesTab customerId={customer.id} customerName={customer.name} />
          </TabsContent>

          {isDemo && (
            <TabsContent value="documents">
              <DocumentsTab customerId={customer.id} customerName={customer.name} />
            </TabsContent>
          )}

          {/* Audit Log Tab */}
          <TabsContent value="audit" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Audit Log
                </CardTitle>
                <CardDescription>Complete chronological history of all customer record changes</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {customerAuditLogs.map((log, index) => {
                    const Icon = getCustomerAuditLogIcon(log.action);
                    const colorClass = getCustomerAuditLogColor(log.action);

                    return (
                      <div key={log.id} className="relative">
                        {index !== customerAuditLogs.length - 1 && (
                          <div className="absolute left-4 top-10 h-full w-0.5 bg-border" />
                        )}
                        <div className="flex gap-4">
                          <div
                            className={cn(
                              'flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0',
                              colorClass,
                            )}
                          >
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1 space-y-2">
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 space-y-1">
                                <p className="font-medium text-sm">{log.description}</p>

                                {(log.action === 'note_added' || log.action === 'note_updated') && log.newValue && (
                                  <div className="text-sm text-muted-foreground">
                                    <p className="italic">&quot;{log.newValue}&quot;</p>
                                  </div>
                                )}

                                {log.action === 'note_deleted' && log.oldValue && (
                                  <div className="text-sm text-muted-foreground">
                                    <p className="italic">&quot;{log.oldValue}&quot;</p>
                                  </div>
                                )}

                                {!log.action.startsWith('note_') &&
                                  log.oldValue !== undefined &&
                                  log.oldValue !== null && (
                                    <div className="flex items-center gap-2 text-sm">
                                      {log.action === 'risk_level_change' && getRiskValueBadgeClass(log.oldValue) ? (
                                        <Badge className={getRiskValueBadgeClass(log.oldValue)!}>
                                          {log.oldValue || 'None'}
                                        </Badge>
                                      ) : (
                                        <Badge variant="outline" className="text-xs">
                                          {log.oldValue || 'None'}
                                        </Badge>
                                      )}
                                      <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                      {log.action === 'risk_level_change' && getRiskValueBadgeClass(log.newValue) ? (
                                        <Badge className={getRiskValueBadgeClass(log.newValue)!}>{log.newValue}</Badge>
                                      ) : (
                                        <Badge variant="outline" className="text-xs">
                                          {log.newValue}
                                        </Badge>
                                      )}
                                    </div>
                                  )}
                              </div>
                              <p className="text-xs text-muted-foreground whitespace-nowrap">
                                {formatDateTime(log.createdAt)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                <User className="h-3 w-3 mr-1" />
                                {log.createdBy}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {customerAuditLogs.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No audit log entries found for this customer.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Transaction Detail Sheet */}
        <TransactionDetailSheet
          open={detailSheetOpen}
          onOpenChange={setDetailSheetOpen}
          transaction={detailTransaction}
        />

        {/* Selection Toolbar - Outstanding Tab */}
        {activeTransactionTab === 'outstanding' && selectedRows.size > 0 && (
          <SelectionToolbar
            selectedCount={selectedRows.size}
            totals={selectionTotals}
            selectedTransactions={selectedTransactions}
            baseCurrency={customerBaseCurrency}
            onClearSelection={handleClearSelection}
            onExportCurrentView={handleOutstandingSelectedExportCurrentView}
            onExportAllColumns={handleOutstandingSelectedExportAllColumns}
            onSendReminders={() => setSendRemindersDialogOpen(true)}
            onAllocateReceipt={handleNavigateToAllocate}
            onSendCopyInvoice={() => setSendCopyInvoiceDialogOpen(true)}
            onCreateCollectionCase={() => setCreateCollectionCaseModalOpen(true)}
            onCreatePromise={() => setCreatePromiseModalOpen(true)}
            onCreateDispute={() => setCreateDisputeModalOpen(true)}
            onAddNote={() => setAddNoteModalOpen(true)}
            onViewAllocationHistory={() => setAllocationHistoryModalOpen(true)}
            onCreateTask={() => setCreateTaskDrawerOpen(true)}
          />
        )}

        {/* Selection Toolbar - History Tab */}
        {activeTransactionTab === 'payment-history' && historySelectedRows.size > 0 && (
          <SelectionToolbar
            selectedCount={historySelectedRows.size}
            totals={historySelectionTotals}
            selectedTransactions={historySelectedTransactions}
            baseCurrency={customerBaseCurrency}
            onClearSelection={handleHistoryClearSelection}
            onExportCurrentView={handleHistorySelectedExportCurrentView}
            onExportAllColumns={handleHistorySelectedExportAllColumns}
            onSendReminders={() => setHistorySendRemindersDialogOpen(true)}
            onAllocateReceipt={handleHistoryNavigateToAllocate}
            onSendCopyInvoice={() => setHistorySendCopyInvoiceDialogOpen(true)}
            onCreateCollectionCase={() => setHistoryCreateCollectionCaseModalOpen(true)}
            onCreatePromise={() => setHistoryCreatePromiseModalOpen(true)}
            onCreateDispute={() => setHistoryCreateDisputeModalOpen(true)}
            onAddNote={() => setHistoryAddNoteModalOpen(true)}
            onViewAllocationHistory={() => setHistoryAllocationHistoryModalOpen(true)}
            onCreateTask={() => setHistoryCreateTaskDrawerOpen(true)}
          />
        )}

        {/* Create Task Drawer - Outstanding Tab */}
        {selectedTransactions.length > 0 && (
          <ContextualCreateTaskDrawer
            open={createTaskDrawerOpen}
            onOpenChange={setCreateTaskDrawerOpen}
            customer={taskDrawerCustomer}
            transactions={selectedTransactions}
            baseCurrency={customerBaseCurrency}
            onCreated={() => setCreateTaskDrawerOpen(false)}
          />
        )}

        {/* Create Task Drawer - History Tab */}
        {historySelectedTransactions.length > 0 && (
          <ContextualCreateTaskDrawer
            open={historyCreateTaskDrawerOpen}
            onOpenChange={setHistoryCreateTaskDrawerOpen}
            customer={taskDrawerCustomer}
            transactions={historySelectedTransactions}
            baseCurrency={customerBaseCurrency}
            onCreated={() => setHistoryCreateTaskDrawerOpen(false)}
          />
        )}

        {/* Send Reminders Dialog - Outstanding Tab */}
        <SendRemindersDialog
          open={sendRemindersDialogOpen}
          onOpenChange={setSendRemindersDialogOpen}
          transactions={selectedTransactions}
          onSend={handleSendReminders}
        />

        {/* Send Reminders Dialog - History Tab */}
        <SendRemindersDialog
          open={historySendRemindersDialogOpen}
          onOpenChange={setHistorySendRemindersDialogOpen}
          transactions={historySelectedTransactions}
          onSend={handleHistorySendReminders}
        />

        {/* Send Copy Invoice Modal - Outstanding Tab */}
        {selectedTransactions.length > 0 && (
          <TransactionSendCopyInvoiceModal
            open={sendCopyInvoiceDialogOpen}
            onOpenChange={setSendCopyInvoiceDialogOpen}
            transactions={selectedTransactions}
            onSuccess={handleSendCopyInvoiceSuccess}
          />
        )}

        {/* Create Collection Case Drawer - Outstanding Tab */}
        <CreateCaseDrawer
          open={createCollectionCaseModalOpen}
          onOpenChange={setCreateCollectionCaseModalOpen}
          onCaseCreated={handleCreateCollectionCase}
          prePopulatedCustomer={
            selectedTransactions.length > 0 && selectedTransactions[0]
              ? {
                  customerId: selectedTransactions[0].customerId.toString(),
                  customerName: selectedTransactions[0].customerName || '',
                  accountNumber: selectedTransactions[0].accountNumber || '',
                }
              : undefined
          }
          selectedTransactions={selectedTransactions}
          triggerSource="customer-transactions"
        />

        {/* Create Promise to Pay Drawer - Outstanding Tab */}
        <CreatePromiseDrawer
          open={createPromiseModalOpen}
          onOpenChange={setCreatePromiseModalOpen}
          prePopulatedCustomer={
            selectedTransactions.length > 0 && selectedTransactions[0]
              ? {
                  customerId: selectedTransactions[0].customerId.toString(),
                  customerName: selectedTransactions[0].customerName || '',
                  accountNumber: selectedTransactions[0].accountNumber || '',
                }
              : undefined
          }
          prePopulatedTransactions={
            selectedTransactions.length > 0
              ? selectedTransactions.map((t) => ({
                  id: t.id.toString(),
                  headerReference: t.headerReference || t.referenceNumber,
                  invoiceDate: new Date(t.transactionDate),
                  originalAmount: t.amount,
                  balanceRemaining: t.displayOutstandingAmount ?? t.outstandingAmount ?? t.amount,
                  daysOverdue: t.dueDate
                    ? Math.max(
                        0,
                        Math.ceil((new Date().getTime() - new Date(t.dueDate).getTime()) / (1000 * 60 * 60 * 24)),
                      )
                    : 0,
                }))
              : undefined
          }
          triggerSource="action-bar"
          onSave={handleCreatePromise}
        />

        {/* Create Dispute Modal - Outstanding Tab */}
        <CreateDisputeModal
          open={createDisputeModalOpen}
          onOpenChange={setCreateDisputeModalOpen}
          selectedTransactions={selectedTransactions}
          onSave={handleCreateDispute}
        />

        {/* Add Note Modal - Outstanding Tab */}
        <AddNoteModal
          open={addNoteModalOpen}
          onOpenChange={setAddNoteModalOpen}
          selectedTransactions={selectedTransactions}
          onSave={handleAddNote}
        />

        {/* Send Copy Invoice Modal - History Tab */}
        {historySelectedTransactions.length > 0 && (
          <TransactionSendCopyInvoiceModal
            open={historySendCopyInvoiceDialogOpen}
            onOpenChange={setHistorySendCopyInvoiceDialogOpen}
            transactions={historySelectedTransactions}
            onSuccess={handleHistorySendCopyInvoiceSuccess}
          />
        )}

        {/* Create Collection Case Drawer - History Tab */}
        <CreateCaseDrawer
          open={historyCreateCollectionCaseModalOpen}
          onOpenChange={setHistoryCreateCollectionCaseModalOpen}
          onCaseCreated={handleHistoryCreateCollectionCase}
          prePopulatedCustomer={
            historySelectedTransactions.length > 0 && historySelectedTransactions[0]
              ? {
                  customerId: historySelectedTransactions[0].customerId.toString(),
                  customerName: historySelectedTransactions[0].customerName || '',
                  accountNumber: historySelectedTransactions[0].accountNumber || '',
                }
              : undefined
          }
          selectedTransactions={historySelectedTransactions}
          triggerSource="customer-transactions"
        />

        {/* Create Promise to Pay Drawer - History Tab */}
        <CreatePromiseDrawer
          open={historyCreatePromiseModalOpen}
          onOpenChange={setHistoryCreatePromiseModalOpen}
          prePopulatedCustomer={
            historySelectedTransactions.length > 0 && historySelectedTransactions[0]
              ? {
                  customerId: historySelectedTransactions[0].customerId.toString(),
                  customerName: historySelectedTransactions[0].customerName || '',
                  accountNumber: historySelectedTransactions[0].accountNumber || '',
                }
              : undefined
          }
          prePopulatedTransactions={
            historySelectedTransactions.length > 0
              ? historySelectedTransactions.map((t) => ({
                  id: t.id.toString(),
                  headerReference: t.headerReference || t.referenceNumber,
                  invoiceDate: new Date(t.transactionDate),
                  originalAmount: t.amount,
                  balanceRemaining: t.displayOutstandingAmount ?? t.outstandingAmount ?? t.amount,
                  daysOverdue: t.dueDate
                    ? Math.max(
                        0,
                        Math.ceil((new Date().getTime() - new Date(t.dueDate).getTime()) / (1000 * 60 * 60 * 24)),
                      )
                    : 0,
                }))
              : undefined
          }
          triggerSource="action-bar"
          onSave={handleHistoryCreatePromise}
        />

        {/* Create Dispute Modal - History Tab */}
        <CreateDisputeModal
          open={historyCreateDisputeModalOpen}
          onOpenChange={setHistoryCreateDisputeModalOpen}
          selectedTransactions={historySelectedTransactions}
          onSave={handleHistoryCreateDispute}
        />

        {/* Add Note Modal - History Tab */}
        <AddNoteModal
          open={historyAddNoteModalOpen}
          onOpenChange={setHistoryAddNoteModalOpen}
          selectedTransactions={historySelectedTransactions}
          onSave={handleHistoryAddNote}
        />

        {/* Allocation History Modal - Outstanding Tab */}
        <AllocationHistoryModal
          open={allocationHistoryModalOpen}
          onOpenChange={setAllocationHistoryModalOpen}
          transactionId={selectedTransactions.length > 0 ? selectedTransactions[0].id : null}
        />

        {/* Allocation History Modal - History Tab */}
        <AllocationHistoryModal
          open={historyAllocationHistoryModalOpen}
          onOpenChange={setHistoryAllocationHistoryModalOpen}
          transactionId={historySelectedTransactions.length > 0 ? historySelectedTransactions[0].id : null}
        />

        {/* On Stop Note Dialog */}
        <OnStopNoteDialog
          open={onStopNoteDialogOpen}
          onOpenChange={setOnStopNoteDialogOpen}
          customerName={customer?.name || ''}
          onSave={handleSaveOnStopNote}
        />

        {/* Strategy with email actions assigned to a customer without a primary contact */}
        <AlertDialog open={strategyEmailWarningOpen} onOpenChange={setStrategyEmailWarningOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>No primary contact set</AlertDialogTitle>
              <AlertDialogDescription>{STRATEGY_EMAIL_NO_PRIMARY_CONTACT_WARNING}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction
                onClick={() => {
                  setStrategyEmailWarningOpen(false);
                  void saveCollectionSettings();
                }}
              >
                OK
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  } catch (error) {
    console.error('Error rendering customer detail page:', error);
    return (
      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-red-600 mb-2">Rendering Error</h2>
            <p className="text-muted-foreground mb-4">
              {error instanceof Error ? error.message : 'An unknown error occurred while rendering the page'}
            </p>
            <p className="text-sm text-muted-foreground mb-4">Customer: {customer?.name || 'Unknown'}</p>
            <Button onClick={() => navigate('/customers')}>Back to Customers</Button>
          </div>
        </div>
      </div>
    );
  }
}
